/* eslint-disable react-refresh/only-export-components */
import BaseLayout from '@nankai/admin/layouts';
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  useRouteError
} from 'react-router-dom';
import { PageModeEnum } from '../constants';
import NotFound from '../views/NotFound';
import PrivateRoute from './PrivateRoute';

const Login = lazy(() => import('@nankai/admin/views/Login'));
const PasswordReset = lazy(
  () => import('@nankai/admin/views/Password/Reset/Reset')
);
const ResetPasswordConfirm = lazy(
  () => import('@nankai/admin/views/Password/ResetConfirm')
);
const UpdatePassword = lazy(
  () => import('@nankai/admin/views/Password/Update')
);
const UserList = lazy(() => import('@nankai/admin/views/User/List'));
const OperationList = lazy(() => import('@nankai/admin/views/Operation/List'));
const Dashboard = lazy(() => import('@nankai/admin/views/Dashboard'));
const ApplicationList = lazy(
  () => import('@nankai/admin/views/Application/List')
);
const TrainList = lazy(() => import('@nankai/admin/views/Train/List'));
const SeatAdjustmentList = lazy(
  () => import('@nankai/admin/views/SeatAdjustment/List')
);
const SeatClassDetail = lazy(
  () => import('@nankai/admin/views/SeatClass/Detail')
);
const SeatAdjustmentDetail = lazy(
  () => import('@nankai/admin/views/SeatAdjustment/Detail')
);
const StationStopPatternList = lazy(
  () => import('@nankai/admin/views/StationStopPattern/List')
);
const StationStopPatternDetail = lazy(
  () => import('@nankai/admin/views/StationStopPattern/Detail')
);
const TrainScheduleList = lazy(
  () => import('@nankai/admin/views/TrainSchedule/List')
);
const TrainServiceList = lazy(
  () => import('@nankai/admin/views/TrainService/List')
);
const TrainAssignmentList = lazy(
  () => import('@nankai/admin/views/TrainAssignment/List')
);
const TrainAssignmentDetail = lazy(
  () => import('@nankai/admin/views/TrainAssignment/Detail')
);
const TrainAssignmentSeatAdjustmentPattern = lazy(
  () => import('@nankai/admin/views/TrainAssignment/SeatAdjustmentPattern/List')
);
const CalendarList = lazy(() => import('@nankai/admin/views/Calendar/List'));
const CalendarDetail = lazy(
  () => import('@nankai/admin/views/Calendar/Detail')
);
const SuspensionList = lazy(
  () => import('@nankai/admin/views/Suspension/List')
);
const SuspensionDetail = lazy(
  () => import('@nankai/admin/views/Suspension/Detail')
);

const InventoryTrainList = lazy(
  () => import('@nankai/admin/views/Inventory/Train/List')
);
const OrderList = lazy(() => import('@nankai/admin/views/Order/List'));
const OrderDetail = lazy(() => import('@nankai/admin/views/Order/Detail'));
const OrderChangeHistoryDetail = lazy(
  () => import('@nankai/admin/views/Order/ChangeHistory/Detail')
);

const OrderSeatCreateDetail = lazy(
  () => import('@nankai/admin/views/Order/Seat/Create/Detail')
);
const OrderSeatCreateConfirm = lazy(
  () => import('@nankai/admin/views/Order/Seat/Create/Confirm/Detail')
);

const OrderSeatChangeDetail = lazy(
  () => import('@nankai/admin/views/Order/Seat/Change/Detail')
);
const OrderSeatReleaseDetail = lazy(
  () => import('@nankai/admin/views/Order/Seat/Release/Detail')
);

const SeatAvailability = lazy(
  () => import('@nankai/admin/views/SeatAvailability')
);

const SpecialSeatAdjustmentDetail = lazy(
  () => import('@nankai/admin/views/SpecialSeatAdjustment/Detail')
);

const StationSeatingTimetable = lazy(
  () => import('@nankai/admin/views/StationSeatingTimetable/Detail')
);

const SeatOrderHistory = lazy(
  () => import('@nankai/admin/views/SeatOrderHistory/SeatSelect')
);

const SeatOrderHistoryDetail = lazy(
  () => import('@nankai/admin/views/SeatOrderHistory/Detail')
);

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback="Loading...">{view}</Suspense>;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route path="/login" element={lazyView(<Login />)} />
      <Route path="/password/reset" element={lazyView(<PasswordReset />)} />
      <Route
        path="/reset-password-confirm"
        element={lazyView(<ResetPasswordConfirm />)}
      />
      <Route element={<PrivateRoute />}>
        <Route path="/" element={<BaseLayout />}>
          <Route path="dashboard" element={lazyView(<Dashboard />)} />
          <Route path="applies" element={lazyView(<ApplicationList />)} />
          <Route path="users" element={lazyView(<UserList />)} />
          <Route path="operations" element={lazyView(<OperationList />)} />
          <Route
            path="/password/update"
            element={lazyView(<UpdatePassword />)}
          />
          {/* 座席調整 */}
          <Route path="trains">
            <Route index element={lazyView(<TrainList />)} />
            <Route
              path=":trainId/seat-classes"
              element={lazyView(<SeatClassDetail />)}
            />
            <Route
              path=":trainId/seat-adjustments"
              element={lazyView(<SeatAdjustmentList />)}
            />
            <Route path=":trainId/seat-adjustments">
              <Route
                path="create"
                element={lazyView(
                  <SeatAdjustmentDetail pageMode={PageModeEnum.Create} />
                )}
              />
              <Route path=":adjustmentId">
                <Route
                  path="edit"
                  element={lazyView(
                    <SeatAdjustmentDetail pageMode={PageModeEnum.Update} />
                  )}
                />
                <Route
                  path="copy"
                  element={lazyView(
                    <SeatAdjustmentDetail pageMode={PageModeEnum.Copy} />
                  )}
                />
                <Route
                  index
                  element={lazyView(
                    <SeatAdjustmentDetail pageMode={PageModeEnum.ReadOnly} />
                  )}
                />
              </Route>
            </Route>
          </Route>
          {/* 停車駅パターン */}
          <Route path="station-stop-patterns">
            <Route index element={lazyView(<StationStopPatternList />)} />
            <Route
              path=":stationStopPatternId"
              element={lazyView(<StationStopPatternDetail />)}
            />
          </Route>
          {/* 座指ダイヤ */}
          <Route path="train-schedules">
            <Route index element={lazyView(<TrainScheduleList />)} />
          </Route>
          {/* 座指ダイヤ登録 */}
          <Route path="train-services/:trainScheduleId">
            <Route index element={lazyView(<TrainServiceList />)} />
          </Route>
          {/* 車両運用確定処理 */}
          <Route path="train-assignments">
            <Route index element={lazyView(<TrainAssignmentList />)} />
            <Route
              path="create"
              element={lazyView(<TrainAssignmentDetail />)}
            />
            <Route
              path=":trainAssignmentId"
              element={lazyView(<TrainAssignmentDetail />)}
            />
            <Route
              path="seat-adjustment-pattern"
              element={lazyView(<TrainAssignmentSeatAdjustmentPattern />)}
            />
          </Route>
          {/* カレンダー */}
          <Route path="calendars">
            <Route index element={lazyView(<CalendarList />)} />
            <Route path=":calendarId" element={lazyView(<CalendarDetail />)} />
          </Route>
          {/* 運休 */}
          <Route path="suspensions">
            <Route index element={lazyView(<SuspensionList />)} />
            <Route
              path="create"
              element={lazyView(
                <SuspensionDetail pageMode={PageModeEnum.Create} />
              )}
            />
            <Route
              path=":suspensionId"
              element={lazyView(
                <SuspensionDetail pageMode={PageModeEnum.Update} />
              )}
            />
          </Route>

          {/* 時刻表座席 */}
          <Route
            path="station-seating-timetable"
            element={lazyView(<StationSeatingTimetable />)}
          />

          <Route path="orders">
            {/* 予約一覧 */}
            <Route index element={lazyView(<OrderList />)} />

            {/* 座席登録 */}
            <Route
              path="seats/:trainAssignmentDetailId"
              element={lazyView(<OrderSeatCreateDetail />)}
            />

            {/* 座席登録確認 */}
            <Route
              path="seats/create"
              element={lazyView(<OrderSeatCreateConfirm />)}
            />

            {/* 座席開放 */}
            <Route
              path=":orderId/release"
              element={lazyView(<OrderSeatReleaseDetail />)}
            />

            {/* 座席変更 */}
            <Route
              path=":orderId/change"
              element={lazyView(<OrderSeatChangeDetail />)}
            />

            {/** 座席開放、座席変更詳細 共通 */}
            <Route path=":orderId" element={lazyView(<OrderDetail />)} />

            {/* 予約変更履歴詳細 */}
            <Route
              path=":orderId/change/:eventId"
              element={lazyView(<OrderChangeHistoryDetail />)}
            />
          </Route>
          <Route path="inventory">
            {/* 列車予約受付 */}
            <Route path="trains" element={lazyView(<InventoryTrainList />)} />
          </Route>

          <Route
            path="seat-availability"
            element={lazyView(<SeatAvailability />)}
          />

          {/* 席扱い区分変更 */}
          <Route path="special-seat-adjustments">
            <Route index element={lazyView(<SpecialSeatAdjustmentDetail />)} />
          </Route>

          {/* Seat Order History */}
          <Route
            path="seat-order-history"
            element={lazyView(<SeatOrderHistory />)}
          />
          <Route
            path="seat-order-history/:trainAssignmentDetailId"
            element={lazyView(<SeatOrderHistoryDetail />)}
          />
        </Route>
        <Route path="404" element={lazyView(<NotFound />)} />
        <Route path="*" element={lazyView(<NotFound />)} />
      </Route>
    </Route>
  )
);

export default router;
